<template>
    <div>
        <b-modal ref="FacilityInfoPopup" title="계측기 정보" size="xl" hide-footer>
            <div class="facility-info-popup">
                <!-- 기본 정보 -->
                <div class="section">
                    <div class="section-header">기본 정보</div>
                    <table class="table table-bordered table-sm">
                        <colgroup>
                            <col width="20%" />
                            <col width="30%" />
                            <col width="20%" />
                            <col width="30%" />
                        </colgroup>
                        <tbody>
                            <tr>
                                <th class="text-center bg-light">제조사</th>
                                <td>{{ facilityOpInfo?.manufacturer || "-" }}</td>
                                <th class="text-center bg-light">모델명</th>
                                <td>{{ facilityOpInfo?.modelNumber || "-" }}</td>
                            </tr>
                            <tr>
                                <th class="text-center bg-light">시리얼 넘버</th>
                                <td>{{ facilityOpInfo?.serialNumber || "-" }}</td>
                                <th class="text-center bg-light">설치장소</th>
                                <td>{{ facilityOpInfo?.location || "-" }}</td>
                            </tr>
                            <tr>
                                <th class="text-center bg-light">정격용량</th>
                                <td>
                                    {{ facilityOpInfo?.ratedCapacity ? facilityOpInfo.ratedCapacity : "-" }}
                                    <span v-if="facilityOpInfo?.capacityUnit">{{
                                        " " + facilityOpInfo.capacityUnit
                                    }}</span>
                                </td>
                                <th class="text-center bg-light">정격 COP</th>
                                <td>{{ facilityOpInfo?.ratedCOP || "-" }}</td>
                            </tr>
                            <tr>
                                <th class="text-center bg-light">최종 수정</th>
                                <td>{{ facilityOpInfo?.updDt || "-" }}</td>
                                <th class="text-center bg-light">설명</th>
                                <td>
                                    <div class="desc-text">{{ facilityOpInfo?.description || "-" }}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="image-section">
                    <div class="section-header">계측기 이미지</div>
                    <div class="image-container">
                        <div v-if="thumbnailImages.length > 0" class="image-grid">
                            <div v-for="(image, index) in thumbnailImages" :key="index" class="image-item">
                                <div class="image-preview" @click="openImageInNewWindow(image)">
                                    <img :src="image.url" :alt="`이미지 ${index + 1}`" class="preview-img" />

                                    <div class="image-info-overlay">
                                        <span class="image-name">{{ image.name }}</span>
                                        <span class="image-size">{{ formatFileSize(image.size) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="no-images-message">
                            <div class="no-images-content">
                                <i class="fa fa-image no-images-icon"></i>
                                <p class="no-images-text">존재하는 이미지가 없습니다</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 검교정 이력 -->
                <div class="section">
                    <div class="section-header">검교정 이력</div>
                    <TableList
                        :title="'검교정 이력'"
                        :columns="clbrtnColumns"
                        :rows="clbrtnHistList || []"
                        :keyField="'clbrtnIdx'"
                        :useFilter="false"
                        :fixedHeader="true"
                        :useMaxHeight="true"
                        :customMaxHeight="'300px'"
                        :mode="'view'"
                    />
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi";
import TableList from "@src/views/component/v2.1/ListDetailView/TableList.vue";

export default {
    props: ["facilityIdx"],
    components: { TableList },
    data() {
        return {
            facilityOpInfo: null,
            clbrtnHistList: [],
            clbrtnColumns: [
                { field: "processDate", label: "처리일자" },
                { field: "clbrnTypeCodeName", label: "유형" },
                { field: "clbrnOrg", label: "기관" },
                { field: "clbrnWorker", label: "담당자" },
                { field: "clbrnDescrb", label: "검교정 내역" },
            ],
            thumbnailImages: [],
        };
    },
    mounted() {
        if (this.facilityIdx) {
            this.getTotalData();
        }
    },
    watch: {
        facilityIdx(newVal) {
            if (newVal) {
                this.getTotalData();
            }
        },
    },

    methods: {
        formatFileSize(bytes) {
            if (bytes === 0) return "0 Bytes";
            const k = 1024;
            const sizes = ["Bytes", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
        },
        async loadFacilityImages(metadata) {
            this.thumbnailImages = [];
            const existingImages = this.getInstImage(metadata);

            for (let i = 0; i < existingImages.length; i++) {
                const imageName = existingImages[i];
                if (imageName) {
                    const imageData = await this.processImageData(imageName, i, `thumbnail-${i + 1}.jpg`);
                    if (imageData) {
                        this.thumbnailImages.push(imageData);
                    }
                }
            }
        },
        async processImageData(imageName, index, thumbnailName) {
            try {
                const imageResponse = await this.getThumbnailImage(imageName, thumbnailName);
                if (imageResponse && imageResponse.url) {
                    return {
                        name: imageName,
                        size: imageResponse.blob ? imageResponse.blob.size : 0,
                        url: imageResponse.url,
                        blob: imageResponse.blob,
                        position: index + 1,
                    };
                }
            } catch (error) {
                console.warn(`이미지 로드 실패 (${imageName}):`, error);
            }
        },
        // 썸네일 이미지 가져오기
        async getThumbnailImage(imageName, thumbnailName) {
            const response = await this.getFacilityOpInfoImage(thumbnailName);
            return response;
        },

        // 실제 원본 이미지 가져오기
        async getOriginalImage(imageName) {
            const response = await this.getFacilityOpInfoImage(imageName);
            return response;
        },
        // 이미지 가져오기 API 호출 함수
        async getFacilityOpInfoImage(imageName) {
            try {
                const response = await backEndApi.facility.getFacilityOpInfoImage(this.facilityIdx, imageName);

                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }

                // CSP 문제 해결을 위해 Base64로 변환하여 URL 생성
                const imageUrl = await this.createImageUrl(response.data);

                return {
                    url: imageUrl,
                    blob: response.data,
                };
            } catch (error) {
                this.alertWarning(error);
                console.error("getFacilityOpInfoImage 오류:", error);
                return null;
            }
        },
        // Blob을 Base64로 변환하는 메서드
        blobToBase64(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        },
        // CSP 문제 해결을 위한 이미지 URL 생성
        async createImageUrl(blob) {
            try {
                const base64 = await this.blobToBase64(blob);
                return base64;
            } catch (error) {
                console.error("Base64 변환 실패:", error);
                return URL.createObjectURL(blob);
            }
        },
        getInstImage(metadata) {
            if (!metadata) return [];
            return ["instImage1", "instImage2", "instImage3"]
                .map((key) => metadata[key])
                .filter((img) => !!img && img.length > 0);
        },

        async openImageInNewWindow(image) {
            try {
                // 원본 이미지명에서 PNG 이미지 가져오기
                const originalImageName = image.name;
                // const pngImageName = this.generateThumbnailName(originalImageName, "png", "image");

                // PNG 이미지 API 호출
                const imageResponse = await this.getOriginalImage(originalImageName);

                if (imageResponse && imageResponse.url) {
                    // 새창 열기
                    // 화면을 최대한 풀사이즈로 새 창을 엽니다
                    const newWindow = window.open(
                        "",
                        "_blank",
                        `left=0,top=0,width=${window.screen.availWidth},height=${window.screen.availHeight},scrollbars=yes`
                    );

                    if (newWindow) {
                        // 새창에 이미지 표시
                        newWindow.document.write(`
                             <!DOCTYPE html>
                             <html>
                             <head>
                                 <title>${originalImageName} - 상세보기</title>
                            <style>
                                     body {
                                         margin: 0;
                                         padding: 20px;
                                         background: #f5f5f5;
                                         font-family: Arial, sans-serif;
                                         display: flex;
                                         flex-direction: column;
                                         align-items: center;
                                         min-height: 100vh;
                                     }
                                     .image-container {
                                         background: white;
                                         padding: 20px;
                                         border-radius: 8px;
                                         box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                                         text-align: center;
                                         max-width: 90%;
                                     }
                                     
                                     .image-display {
                                         max-width: 100%;
                                         height: auto;
                                         border-radius: 4px;
                                         box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                                     }
                                
                                     
                                 </style>
                             <body>
                                 <div class="image-container">
                                     <img src="${imageResponse.url}" alt="${originalImageName}" class="image-display" />
                                     
                                 </div>
                             </body>
                             </html>
                         `);

                        newWindow.document.close();
                    } else {
                        // 팝업 차단된 경우 경고
                        this.alertWarning("팝업이 차단되었습니다. 팝업 차단을 해제해주세요.");
                    }
                } else {
                    this.alertWarning("이미지를 불러올 수 없습니다.");
                }
            } catch (error) {
                console.error("이미지 새창 열기 실패:", error);
                this.alertWarning("이미지를 열 수 없습니다.");
            }
        },
        async showModal() {
            this.$refs.FacilityInfoPopup.show();
        },
        // 부모 호환용
        async show() {
            await this.showModal();
        },
        hideModal() {
            this.$refs.FacilityInfoPopup.hide();
        },
        async getTotalData() {
            this.facilityOpInfo = await this.getFacilityOpInfo();
            this.clbrtnHistList = await this.getClbrtnHistListByFacility();
            await this.loadFacilityImages(this.facilityOpInfo);
        },
        async getClbrtnHistListByFacility() {
            if (!this.facilityIdx) {
                return null;
            }
            try {
                const response = await backEndApi.clbrtnHist.getClbrnHistListByFacility(this.facilityIdx);
                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }
                return response.data;
            } catch (error) {
                this.alertWarning(error);
                console.error("getClbrtnHistListByFacility 오류:", error);
                return null;
            }
        },
        async getFacilityOpInfo() {
            if (!this.facilityIdx) {
                return null;
            }
            try {
                const response = await backEndApi.facility.getFacilityOpInfo(this.facilityIdx);

                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }

                return response.data;
            } catch (error) {
                this.alertWarning(error);
                console.error("getFacilityOpInfo 오류:", error);
                return null;
            }
        },
    },
};
</script>

<style scoped>
/* 우측 이미지 섹션 */
.image-section {
    flex: 1;

    position: relative;
    margin-bottom: 16px;
}

/* 구분선 추가 */
.image-section::before {
    content: "";
    position: absolute;
    left: -20px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: linear-gradient(to bottom, transparent, #dee2e6 20%, #dee2e6 80%, transparent);
}
.image-container {
    width: 100%;
}

.image-grid {
    display: flex;
    /* flex-direction: column; */
    gap: 12px;
    max-width: 100%;
}

.image-item {
    display: flex;
    flex-direction: column;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
    transition: all 0.3s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    width: 100%;
}

.image-preview {
    position: relative;
    width: 100%;
    height: 170px;
    overflow: hidden;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
}

.preview-img {
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    background: #e9ecef;
}
.image-overlay {
    position: absolute;
    top: 6px;
    right: 6px;
}
.image-info-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgb(233, 236, 239, 0.5);
    color: black;
    padding: 8px 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    backdrop-filter: blur(3px);
    transition: all 0.3s ease;
}

.image-preview:hover .image-info-overlay {
    background: rgba(233, 236, 239, 0.7);
}

.image-name {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #424242;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.image-size {
    display: block;
    font-size: 10px;
    color: #757575;
}

/* 이미지 없음 메시지 스타일 */
.no-images-message {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 120px;
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    margin: 10px 0;
}

.no-images-content {
    text-align: center;
    color: #6c757d;
}

.no-images-icon {
    font-size: 2rem;
    color: #adb5bd;
    margin-bottom: 8px;
}

.no-images-text {
    font-size: 14px;
    font-weight: 500;
    margin: 0;
    color: #6c757d;
}
.facility-info-popup {
    padding: 8px 4px;
}
.section {
    margin-bottom: 16px;
}
.section-header {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
}
.desc-text {
    max-height: 60px;
    overflow-y: auto;
    white-space: pre-wrap;
}
</style>
